import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    emptyMessage: { type: String, default: "No matches found." },
    placeholder: String,
    resultsLimit: { type: Number, default: 50 }
  }

  connect() {
    if (!(this.element instanceof HTMLSelectElement)) return

    this.select = this.element
    this.required = this.select.required
    this.selectClasses = this.select.className
    this.highlightedIndex = -1
    this.optionIndex = this.buildOptionIndex()
    this.installUi()
    this.syncInputFromSelect()
    this.syncValidity()
    this.observeSelect()

    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this)
    this.boundHandleDocumentFocus = this.handleDocumentFocus.bind(this)
    document.addEventListener("click", this.boundHandleDocumentClick)
    document.addEventListener("focusin", this.boundHandleDocumentFocus)
  }

  disconnect() {
    this.selectObserver?.disconnect()
    document.removeEventListener("click", this.boundHandleDocumentClick)
    document.removeEventListener("focusin", this.boundHandleDocumentFocus)

    if (this.wrapper) {
      this.wrapper.remove()
    }

    if (this.originalId) {
      this.select.id = this.originalId
    }

    this.select.required = this.required
    this.select.classList.remove("hidden")
  }

  buildOptionIndex() {
    return Array.from(this.select.options)
      .filter((option) => option.value !== "")
      .map((option, index) => ({
        index,
        label: option.textContent.trim(),
        searchLabel: option.textContent.trim().toLowerCase(),
        value: option.value
      }))
  }

  installUi() {
    this.originalId = this.select.id
    this.select.classList.add("hidden")
    this.select.required = false

    this.wrapper = document.createElement("div")
    this.wrapper.className = "relative mt-1"

    this.input = document.createElement("input")
    this.input.type = "text"
    this.input.autocomplete = "off"
    this.input.spellcheck = false
    this.input.placeholder = this.resolvedPlaceholder
    this.input.disabled = this.select.disabled
    this.input.required = this.required
    this.input.className = this.selectClasses
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-autocomplete", "list")
    this.input.setAttribute("aria-expanded", "false")

    if (this.originalId) {
      this.input.id = this.originalId
      this.select.removeAttribute("id")
    }

    this.resultsId = `${this.input.id || this.select.name.replace(/[\[\]]+/g, "_")}_results`
    this.input.setAttribute("aria-controls", this.resultsId)

    this.panel = document.createElement("div")
    this.panel.id = this.resultsId
    this.panel.className = "absolute z-30 mt-1 hidden w-full overflow-hidden rounded-lg border border-stone-200/80 dark:border-stone-700 bg-white dark:bg-stone-950 shadow-2xl"

    this.results = document.createElement("div")
    this.results.className = "max-h-72 overflow-y-auto py-1"

    this.panel.appendChild(this.results)
    this.wrapper.appendChild(this.input)
    this.wrapper.appendChild(this.panel)
    this.select.insertAdjacentElement("afterend", this.wrapper)

    this.input.addEventListener("focus", () => {
      this.open()
      this.renderResults()
    })
    this.input.addEventListener("click", () => {
      this.open()
      this.renderResults()
    })
    this.input.addEventListener("input", this.handleInput.bind(this))
    this.input.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  observeSelect() {
    this.selectObserver = new MutationObserver(() => this.syncFromSelect())
    this.selectObserver.observe(this.select, {
      childList: true,
      attributes: true,
      attributeFilter: ["disabled"]
    })
  }

  handleInput() {
    if (this.input.value !== this.selectedLabel) {
      this.clearSelection({ preserveInput: true })
    }

    this.highlightedIndex = 0
    this.open()
    this.renderResults()
    this.syncValidity()
  }

  handleKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      event.stopPropagation()
      this.open()
      this.moveHighlight(1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      event.stopPropagation()
      this.open()
      this.moveHighlight(-1)
      return
    }

    if (event.key === "Enter" && !this.panel.classList.contains("hidden")) {
      event.preventDefault()
      event.stopPropagation()

      const option = this.filteredOptions()[this.highlightedIndex]
      if (option) {
        this.selectOption(option)
      }
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.syncInputFromSelect()
      this.close()
    }
  }

  handleDocumentClick(event) {
    if (this.wrapper.contains(event.target) || event.target === this.select) return

    this.close()
  }

  handleDocumentFocus(event) {
    if (this.wrapper.contains(event.target) || event.target === this.select) return

    this.close()
  }

  moveHighlight(direction) {
    const options = this.filteredOptions()
    if (options.length === 0) {
      this.highlightedIndex = -1
      this.renderResults()
      return
    }

    if (this.highlightedIndex < 0) {
      this.highlightedIndex = direction > 0 ? 0 : options.length - 1
    } else {
      this.highlightedIndex = (this.highlightedIndex + direction + options.length) % options.length
    }

    this.renderResults()
    this.scrollHighlightedOptionIntoView()
  }

  renderResults() {
    const options = this.filteredOptions()
    this.results.innerHTML = ""

    if (options.length === 0) {
      const emptyState = document.createElement("div")
      emptyState.className = "px-3 py-2 text-sm text-stone-500 dark:text-stone-400"
      emptyState.textContent = this.emptyMessageValue
      this.results.appendChild(emptyState)
      return
    }

    options.forEach((option, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.searchableSelectOption = "true"
      button.dataset.searchableSelectIndex = String(index)
      button.className = "block w-full px-3 py-2 text-left text-sm text-stone-700 dark:text-stone-200"
      button.textContent = option.label

      button.addEventListener("mouseenter", () => {
        this.highlightedIndex = index
        this.refreshHighlightState()
      })
      button.addEventListener("mousedown", (event) => event.preventDefault())
      button.addEventListener("click", () => this.selectOption(option))

      this.results.appendChild(button)
    })

    this.refreshHighlightState()
  }

  selectOption(option) {
    this.select.value = option.value
    this.input.value = option.label
    this.highlightedIndex = -1
    this.select.dispatchEvent(new Event("change", { bubbles: true }))
    this.syncValidity()
    this.close()
  }

  clearSelection({ preserveInput } = { preserveInput: false }) {
    this.select.value = ""

    if (!preserveInput) {
      this.input.value = ""
    }
  }

  filteredOptions() {
    const rawQuery = this.input.value.trim().toLowerCase()
    const query = rawQuery !== "" && rawQuery === this.selectedLabel.toLowerCase() ? "" : rawQuery
    const limit = this.resultsLimitValue

    if (query === "") {
      return this.optionIndex.slice(0, limit)
    }

    const prefixMatches = []
    const containsMatches = []

    this.optionIndex.forEach((option) => {
      if (!option.searchLabel.includes(query)) return

      if (option.searchLabel.startsWith(query)) {
        prefixMatches.push(option)
      } else {
        containsMatches.push(option)
      }
    })

    return prefixMatches.concat(containsMatches).slice(0, limit)
  }

  syncInputFromSelect() {
    this.input.value = this.selectedLabel
    this.syncValidity()
  }

  syncFromSelect() {
    this.optionIndex = this.buildOptionIndex()
    this.input.disabled = this.select.disabled
    this.input.placeholder = this.resolvedPlaceholder
    this.syncInputFromSelect()
    this.close()
  }

  syncValidity() {
    if (!this.required) {
      this.input.setCustomValidity("")
      return
    }

    if (this.select.value !== "") {
      this.input.setCustomValidity("")
      return
    }

    if (this.input.value.trim() === "") {
      this.input.setCustomValidity("Please select an option.")
    } else {
      this.input.setCustomValidity("Choose a matching option from the list.")
    }
  }

  open() {
    if (this.input.disabled) return

    this.panel.classList.remove("hidden")
    this.input.setAttribute("aria-expanded", "true")
  }

  close() {
    this.panel.classList.add("hidden")
    this.input.setAttribute("aria-expanded", "false")
    this.highlightedIndex = -1
  }

  scrollHighlightedOptionIntoView() {
    const highlighted = this.results.querySelector(`[data-searchable-select-index="${this.highlightedIndex}"]`)
    highlighted?.scrollIntoView({ block: "nearest" })
  }

  refreshHighlightState() {
    this.results.querySelectorAll("[data-searchable-select-index]").forEach((element) => {
      const isHighlighted = Number(element.dataset.searchableSelectIndex) === this.highlightedIndex
      element.classList.toggle("bg-stone-100", isHighlighted)
      element.classList.toggle("text-stone-900", isHighlighted)
      element.classList.toggle("dark:bg-stone-800", isHighlighted)
      element.classList.toggle("dark:text-stone-100", isHighlighted)
      element.classList.toggle("text-stone-700", !isHighlighted)
      element.classList.toggle("dark:text-stone-200", !isHighlighted)
    })
  }

  get blankOption() {
    return Array.from(this.select.options).find((option) => option.value === "")
  }

  get blankOptionLabel() {
    return this.blankOption?.textContent?.trim()
  }

  get selectedLabel() {
    const selectedOption = this.select.selectedOptions[0]
    return selectedOption?.value ? selectedOption.textContent.trim() : ""
  }

  get resolvedPlaceholder() {
    if (this.optionIndex.length === 0 && this.blankOptionLabel) {
      return this.blankOptionLabel
    }

    return this.placeholderValue || this.blankOptionLabel || "Search options"
  }
}
